'use client'

/**
 * Root error boundary. `app/error.tsx` only catches errors BELOW the root
 * layout — an error thrown IN the root layout (providers, theme, auth bootstrap)
 * or during React render escapes it entirely and the user gets a blank page with
 * nothing reported. global-error.tsx is the only thing that catches those.
 *
 * We had error.tsx but no global-error.tsx, so exactly the worst class of bug —
 * "the whole app is white" — was the one going unreported.
 *
 * It replaces the root layout, so it renders its own <html> and <body>, and
 * none of the app's providers or stylesheets exist here. The view
 * (components/status/global-error-view.tsx) is the product's 500 page built
 * to stand on its own: its own styles, its own picture, the reader's saved
 * language and direction.
 */
import { GlobalErrorView, useStoredLocaleCatalogue } from '@/components/status/global-error-view'
import { directionFor } from '@/lib/i18n/direction'
import { useCaptureCaughtError } from '@/lib/sentry/capture-caught-error'

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string }
}) {
  // Sentry, once, with the digest the view shows as its reference — the same
  // report RouteErrorPanel makes for every boundary below the root.
  useCaptureCaughtError(error)

  const { locale, messages } = useStoredLocaleCatalogue()

  return (
    <html lang={locale} dir={directionFor(locale)}>
      <body style={{ margin: 0 }}>
        <GlobalErrorView locale={locale} messages={messages} digest={error.digest} />
      </body>
    </html>
  )
}
